import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
`;

/* ───────────────────────── Shell ───────────────────────── */

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
  color: #0a0010;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 5vw, 3.5rem);
`;

/* ───────────────────────── Hero ───────────────────────── */

const Hero = styled.section`
  padding: clamp(6rem, 12vw, 8.5rem) 0 clamp(2.5rem, 5vw, 3.5rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.12);
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.6rem;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.4rem, 5.5vw, 4.2rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 1.2rem;
`;

const HeroMeta = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
`;

const HeroLede = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(10, 0, 16, 0.65);
  max-width: 640px;
  margin-top: 1.6rem;
`;

const AcceptBanner = styled.div`
  margin-top: 1.8rem;
  padding: 1.1rem 1.4rem;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border: 1px solid rgba(168, 85, 247, 0.18);
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.7;
  color: #4c1d75;
  max-width: 760px;
`;

/* ───────────────────────── Layout ───────────────────────── */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: clamp(2rem, 4vw, 5rem);
  padding: clamp(3rem, 6vw, 4.5rem) 0 6rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TocWrap = styled.nav`
  position: sticky;
  top: clamp(1.5rem, 4vh, 2.5rem);
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.25);
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    position: static;
    max-height: none;
    margin-bottom: 1rem;
  }
`;

const TocTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
  margin-bottom: 1rem;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const TocLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.83rem;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10, 0, 16, 0.55)")};
  text-decoration: none;
  display: block;
  line-height: 1.4;
  border-left: 2px solid
    ${({ $active }) => ($active ? "#a855f7" : "transparent")};
  padding-left: 0.75rem;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: #7c3aed;
  }
`;

const Content = styled.div`
  max-width: 760px;
`;

const Section = styled.section`
  scroll-margin-top: 2rem;
  padding-bottom: clamp(2.2rem, 4vw, 3rem);
  margin-bottom: clamp(2.2rem, 4vw, 3rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.3rem, 2.2vw, 1.6rem);
  font-weight: 600;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;

  span {
    font-size: 0.75rem;
    font-weight: 500;
    color: #a855f7;
    letter-spacing: 0.05em;
  }
`;

const SubTitle = styled.h3`
  font-size: 1.02rem;
  font-weight: 600;
  margin: 1.6rem 0 0.7rem;
  color: #1a0b2e;
`;

const P = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.85;
  color: rgba(10, 0, 16, 0.68);
  margin-bottom: 0.9rem;

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0.6rem 0 1rem;
`;

const ListItem = styled.li`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(10, 0, 16, 0.68);
  padding-left: 1.2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #a855f7;
  }

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const Callout = styled.div`
  background: rgba(168, 85, 247, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 1rem 0 1.4rem;
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  min-width: 560px;

  th {
    text-align: left;
    font-weight: 600;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7c3aed;
    background: rgba(168, 85, 247, 0.07);
    padding: 0.8rem 1rem;
    border-bottom: 1px solid rgba(168, 85, 247, 0.15);
  }

  td {
    padding: 0.8rem 1rem;
    color: rgba(10, 0, 16, 0.68);
    font-weight: 300;
    line-height: 1.6;
    border-bottom: 1px solid rgba(168, 85, 247, 0.08);
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }

  td:first-child {
    font-weight: 600;
    color: #0a0010;
    white-space: nowrap;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.8rem 0 1rem;
`;

const ContactRow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  color: rgba(10, 0, 16, 0.68);

  b {
    color: #0a0010;
    font-weight: 600;
  }

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

/* Highlighted hyperlink used specifically for every reference to the
   Community Guidelines, so the cross-reference stands out visually and
   not just by color — a soft purple "mark" background plus underline. */
const GuidelinesLink = styled(Link)`
  color: #6d28d9;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(109, 40, 217, 0.4);
  text-underline-offset: 2px;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.16),
    rgba(124, 58, 237, 0.16)
  );
  padding: 0.05em 0.35em;
  border-radius: 5px;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(232, 121, 249, 0.28),
      rgba(124, 58, 237, 0.28)
    );
    color: #4c1d75;
  }
`;

const BackHome = styled(Link)`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  color: #7c3aed;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

/* ───────────────────────── Data ───────────────────────── */

const TOC = [
  { id: "scope", label: "1. Scope & Who This Applies To" },
  { id: "collect", label: "2. Information We Collect" },
  { id: "use", label: "3. How We Use Your Information" },
  { id: "legalbasis", label: "4. Legal Bases for Processing" },
  { id: "retention", label: "5. How Long We Keep Information" },
  { id: "sharing", label: "6. How We Share Information" },
  { id: "protect", label: "7. How We Protect Information" },
  { id: "rights", label: "8. Your Choices & Rights" },
  { id: "comms", label: "9. Communications & Notifications" },
  { id: "transfers", label: "10. International Data Transfers" },
  { id: "children", label: "11. Children's Privacy" },
  { id: "changes", label: "12. Changes to This Policy" },
  { id: "contact", label: "13. How to Contact Us" },
];

export default function PrivacyPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Legal</Eyebrow>
          <HeroTitle>Privacy Policy</HeroTitle>
          <HeroMeta>
            <span>Effective Date: July 9, 2026</span>
            <span>Last Updated: July 9, 2026</span>
          </HeroMeta>
          <HeroLede>
            Data Controller: NUDR INC, a company incorporated under the laws of
            the State of Delaware, U.S.A., with its registered office at 1105 N
            Buena Vista Street, Burbank, CA 91505 ("NudR," "Company," "we,"
            "us," or "our"). This Policy explains how we collect, use, share,
            and protect information about you when you use the NudR mobile
            applications, website, APIs, and related services (the "Service"),
            and is incorporated by reference into the{" "}
            <Link to="/terms" style={{ color: "#7c3aed" }}>
              NudR Terms of Service
            </Link>
            .
          </HeroLede>
          <AcceptBanner>
            Please read this Policy carefully. By creating an account or using
            the Service, you acknowledge that you have read and understood this
            Policy. Where required by applicable law, we obtain your consent to
            specific processing activities as described below.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            {/* 1. Scope */}
            <Section id="scope">
              <SectionTitle>
                <span>01</span>Scope and Who This Applies To
              </SectionTitle>
              <P>
                This Policy applies to all users of the Service. The Service is
                intended solely for individuals who are at least 18 years old;
                we do not knowingly collect personal information from anyone
                under 18. If we learn that we have collected personal
                information from a person under 18, we will delete it and
                terminate the associated account. See Section 11 (Children's
                Privacy).
              </P>
            </Section>

            {/* 2. Collect */}
            <Section id="collect">
              <SectionTitle>
                <span>02</span>Information We Collect
              </SectionTitle>
              <P>
                We collect the following categories of information. Fields
                marked as required are necessary to create and operate your
                account; optional fields are provided at your discretion.
              </P>

              <SubTitle>2.1 Information You Provide at Registration</SubTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Examples</th>
                      <th>Required or optional?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Identity & profile</td>
                      <td>
                        Legal name (used for verification; immutable after
                        signup), username/display name, email address, date of
                        birth, gender, height, bio, zodiac, education,
                        occupation
                      </td>
                      <td>
                        Required: name, email, date of birth, gender, height,
                        bio, zodiac. Optional: education, occupation
                      </td>
                    </tr>
                    <tr>
                      <td>Account credentials</td>
                      <td>
                        Password (stored only as a salted cryptographic hash —
                        never in plaintext)
                      </td>
                      <td>Required</td>
                    </tr>
                    <tr>
                      <td>Profile photographs</td>
                      <td>
                        One to five profile photos, each with a visibility
                        setting (public or hidden) and a descriptive tag
                      </td>
                      <td>
                        Required: at least one photo. Optional: additional
                        photos
                      </td>
                    </tr>
                    <tr>
                      <td>Identity-verification photo</td>
                      <td>
                        A government-issued identification photograph, submitted
                        for identity verification
                      </td>
                      <td>Required</td>
                    </tr>
                    <tr>
                      <td>Matching preferences</td>
                      <td>
                        Age range, distance radius, and gender(s) you are
                        looking for; and role/vibe/intensity/comfort attributes
                        used by the matching experience
                      </td>
                      <td>
                        Required: role/vibe/intensity/comfort attributes.
                        Optional: match preferences (defaults applied if
                        omitted)
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <Callout>
                <P>
                  <strong>Sensitive information.</strong> Some information you
                  provide — such as your identity-verification document, and
                  profile details that may reveal or imply characteristics like
                  gender — may be treated as sensitive personal information
                  under applicable law. Your government identification
                  photograph is{" "}
                  <strong>
                    encrypted at rest using strong public-key and authenticated
                    encryption (RSA-OAEP with AES-GCM)
                  </strong>{" "}
                  and stored separately from your public profile. We use it only
                  for identity verification and fraud prevention, not for
                  advertising.
                </P>
              </Callout>

              <SubTitle>2.2 Information We Collect Automatically</SubTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>What we collect and why</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Location</td>
                      <td>
                        Your device's real-time GPS location while the app is in
                        active, foreground use, used for distance-based
                        matching. Where GPS is unavailable, disabled, or fails,
                        we derive an approximate location from your IP address
                        via our network provider (Cloudflare). Location is shown
                        to others only as an approximate area (e.g., a
                        state/region name or distance range), never as exact
                        coordinates.
                      </td>
                    </tr>
                    <tr>
                      <td>Device & connection</td>
                      <td>
                        Device identifier (bound to your session), IP address,
                        and a push-notification (Firebase Cloud Messaging) token
                        if you enable notifications.
                      </td>
                    </tr>
                    <tr>
                      <td>Security & anti-fraud signals</td>
                      <td>
                        IP-based risk signals (e.g., VPN/proxy/Tor indicators,
                        geolocation consistency, request velocity and
                        frequency), device and session integrity signals (used
                        solely to detect fraud and automated abuse, and not for
                        advertising or cross-app tracking), and proof-of-work /
                        anti-bot challenge results, used to detect fraud, abuse,
                        and automated access.
                      </td>
                    </tr>
                    <tr>
                      <td>Usage & interaction</td>
                      <td>
                        Actions such as likes/passes, matches, Rizz Requests,
                        consent grants, reports you file, screenshot-detection
                        events, session activity, and distinct active-day counts
                        (used, for example, to settle referral rewards).
                      </td>
                    </tr>
                    <tr>
                      <td>Payment metadata</td>
                      <td>
                        Subscription status, plan, and transaction records. Card
                        and payment-instrument details are collected and
                        processed directly by our payment processor (Stripe);
                        NudR does not store your full card number.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>

              <SubTitle>2.3 Messages and Shared Media</SubTitle>
              <P>
                Text messages between matched users are{" "}
                <strong>end-to-end encrypted</strong> using keys generated on
                your device; in the ordinary course, NudR cannot read their
                content. We process and briefly store the encrypted message
                payloads and related metadata (such as sender, timestamp,
                delivery status, and expiry) needed to deliver messages across
                your devices. As described in Section 5 (Retention), messages
                and shared media are automatically deleted on a short schedule.
                If you or another participant <strong>report</strong> a message,
                the reported content is decrypted and retained for as long as
                needed to investigate and act on that report.
              </P>
              <P>
                <strong>Photo metadata.</strong> When you upload a photo, we
                strip embedded metadata (such as EXIF/GPS/camera data) before
                storage, and we generate resized/blurred image variants to
                operate features like hidden-photo blurring and content-safety
                scanning.
              </P>

              <SubTitle>2.4 Encryption Key Material</SubTitle>
              <P>
                To support end-to-end encrypted messaging across your devices,
                we store your device public keys and, if you use encrypted key
                backup, encrypted key material (including conversation keys that
                remain encrypted with keys we cannot read). This lets you
                restore secure conversations on a new or reset device without
                exposing message content to NudR.
              </P>
            </Section>

            {/* 3. Use */}
            <Section id="use">
              <SectionTitle>
                <span>03</span>How We Use Your Information
              </SectionTitle>
              <P>We use the information we collect to:</P>
              <List>
                <ListItem>
                  Create, operate, secure, and maintain your account and the
                  Service.
                </ListItem>
                <ListItem>
                  Verify your identity and age, and reduce impersonation, fraud,
                  and abuse.
                </ListItem>
                <ListItem>
                  Power core features — matching and discovery, distance
                  calculations, chat and consent-based media sharing,
                  notifications, and referrals/rewards.
                </ListItem>
                <ListItem>
                  Detect, investigate, and prevent security incidents,
                  fraudulent or unlawful activity, and violations of our{" "}
                  <Link to="/terms" style={{ color: "#7c3aed" }}>
                    Terms
                  </Link>{" "}
                  or{" "}
                  <GuidelinesLink to="/guidelines">
                    Community Guidelines
                  </GuidelinesLink>
                  , including through IP risk scoring and anti-bot measures.
                </ListItem>
                <ListItem>
                  Process subscriptions, trials, renewals, and in-app purchases,
                  and maintain billing records.
                </ListItem>
                <ListItem>
                  Communicate with you about the Service, including service,
                  security, and transactional messages, and — where you have not
                  opted out — product updates.
                </ListItem>
                <ListItem>
                  Comply with legal obligations and enforce our agreements.
                </ListItem>
              </List>
              <Callout>
                <P>
                  <strong>
                    We do not sell your personal information, and we do not use
                    your identity-verification document or the contents of your
                    messages for advertising.
                  </strong>{" "}
                  We do not display third-party advertising that targets you
                  based on your private profile data.
                </P>
              </Callout>
            </Section>

            {/* 4. Legal Basis */}
            <Section id="legalbasis">
              <SectionTitle>
                <span>04</span>Legal Bases for Processing
              </SectionTitle>
              <P>
                Where required by applicable law (for example, for users
                protected by comprehensive privacy statutes), we rely on the
                following legal bases to process your information:
              </P>
              <List>
                <ListItem>
                  <strong>Performance of a contract</strong> — to provide the
                  Service you request under the Terms of Service.
                </ListItem>
                <ListItem>
                  <strong>Legitimate interests</strong> — to secure the Service,
                  prevent fraud and abuse, and improve our features, balanced
                  against your rights.
                </ListItem>
                <ListItem>
                  <strong>Consent</strong> — for processing that requires it,
                  such as accessing your device location or sending certain
                  marketing communications; you may withdraw consent at any
                  time.
                </ListItem>
                <ListItem>
                  <strong>Legal obligation</strong> — to comply with applicable
                  laws and lawful requests.
                </ListItem>
              </List>
            </Section>

            {/* 5. Retention */}
            <Section id="retention">
              <SectionTitle>
                <span>05</span>How Long We Keep Information
              </SectionTitle>
              <P>
                We keep personal information only as long as necessary for the
                purposes described in this Policy, unless a longer period is
                required or permitted by law. Key retention rules include:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Data type</th>
                      <th>Retention</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Chat messages & shared media</td>
                      <td>
                        Deleted within 24 hours after being read/viewed by the
                        recipient, or within 30 days if never read/viewed,
                        whichever comes first. Content tied to an active report
                        is retained longer to investigate.
                      </td>
                    </tr>
                    <tr>
                      <td>Account profile data</td>
                      <td>
                        Retained while your account is active; deleted or
                        de-identified after account deletion, subject to the
                        exceptions below.
                      </td>
                    </tr>
                    <tr>
                      <td>Identity-verification document</td>
                      <td>
                        Retained (encrypted) for as long as needed for
                        verification and fraud-prevention purposes and as
                        required by law.
                      </td>
                    </tr>
                    <tr>
                      <td>Security & IP risk records</td>
                      <td>
                        IP intelligence and related risk records are retained on
                        a rolling basis (approximately 30 days for stale
                        entries) to protect the Service.
                      </td>
                    </tr>
                    <tr>
                      <td>Session tokens</td>
                      <td>
                        Stored only as a hash; expire automatically on a short
                        schedule and on logout, password change, or ban.
                      </td>
                    </tr>
                    <tr>
                      <td>Payment records</td>
                      <td>
                        Retained as needed for accounting, tax, and
                        legal-compliance purposes.
                      </td>
                    </tr>
                    <tr>
                      <td>Safety & enforcement records</td>
                      <td>
                        Records relating to suspensions, bans, and verified
                        reports may be retained after account deletion to
                        enforce our Terms and protect other users.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
            </Section>

            {/* 6. Sharing */}
            <Section id="sharing">
              <SectionTitle>
                <span>06</span>How We Share Information
              </SectionTitle>
              <P>
                We share information only as described below. We do not sell
                your personal information.
              </P>

              <SubTitle>6.1 With Other Users</SubTitle>
              <P>
                Your public profile (such as your display name, photos marked
                public, approximate area, and profile attributes) is visible to
                other users as part of matching and discovery. Information you
                choose to share in chats or via consent-based media is shared
                with your match. If you enable anonymous mode (a paid feature),
                your chosen anonymous name is shown in place of certain profile
                details, subject to its limitations.
              </P>

              <SubTitle>6.2 With Service Providers (Sub-Processors)</SubTitle>
              <P>
                We use trusted third-party providers to operate the Service.
                They may process your information only on our instructions and
                for the purposes we specify. Our key providers include:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Stripe</td>
                      <td>Payment processing and subscription billing</td>
                    </tr>
                    <tr>
                      <td>Cloudflare</td>
                      <td>
                        Network delivery and security (Turnstile anti-bot, edge
                        geolocation, and R2 image storage/caching)
                      </td>
                    </tr>
                    <tr>
                      <td>Firebase Cloud Messaging (Google)</td>
                      <td>Delivery of push notifications</td>
                    </tr>
                    <tr>
                      <td>Supabase</td>
                      <td>Primary application database and object storage</td>
                    </tr>
                    <tr>
                      <td>Amazon Web Services (AWS)</td>
                      <td>
                        Cloud hosting, secrets management, background
                        processing, and delivery of verification,
                        password-reset, and transactional emails
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>

              <SubTitle>6.3 For Legal, Safety, and Security Reasons</SubTitle>
              <P>
                We may disclose information when we reasonably believe it is
                necessary to comply with a law, regulation, legal process, or
                governmental request; to enforce our Terms; to detect, prevent,
                or address fraud, security, or technical issues; or to protect
                the rights, property, or safety of NudR, our users, or the
                public.
              </P>

              <SubTitle>6.4 Business Transfers</SubTitle>
              <P>
                If NudR is involved in a merger, acquisition, financing,
                reorganization, or sale of assets, your information may be
                transferred as part of that transaction, subject to this Policy
                or a successor policy with equivalent protections.
              </P>
            </Section>

            {/* 7. Protect */}
            <Section id="protect">
              <SectionTitle>
                <span>07</span>How We Protect Information
              </SectionTitle>
              <P>
                We apply technical and organizational measures designed to
                protect your information, including:
              </P>
              <List>
                <ListItem>
                  Encryption of data in transit (HTTPS/TLS) and encryption at
                  rest for sensitive data such as identity-verification
                  documents.
                </ListItem>
                <ListItem>
                  End-to-end encryption for the content of chat messages.
                </ListItem>
                <ListItem>
                  Storing passwords only as salted cryptographic hashes and
                  session tokens only as hashes.
                </ListItem>
                <ListItem>
                  Request signing, origin verification, rate limiting,
                  proof-of-work, and anti-bot challenges to resist automated
                  abuse.
                </ListItem>
                <ListItem>
                  Access controls, secrets management, and separation of
                  sensitive data from public profile data.
                </ListItem>
              </List>
              <P>
                <strong>
                  No method of transmission or storage is completely secure,
                </strong>{" "}
                and we cannot guarantee absolute security. You are responsible
                for keeping your credentials and device secure and for notifying
                us of any suspected unauthorized access.
              </P>
            </Section>

            {/* 8. Rights */}
            <Section id="rights">
              <SectionTitle>
                <span>08</span>Your Choices and Rights
              </SectionTitle>

              <SubTitle>8.1 In-App Controls</SubTitle>
              <P>You can, within the Service:</P>
              <List>
                <ListItem>
                  Update most profile information (note: your legal name is
                  immutable after signup and requires a support request with
                  documentation to correct).
                </ListItem>
                <ListItem>
                  Set each photo's visibility to public or hidden, and choose
                  your primary photo.
                </ListItem>
                <ListItem>
                  Manage privacy settings such as read receipts and, for
                  eligible subscribers, anonymous mode.
                </ListItem>
                <ListItem>
                  Manage notification preferences (new matches, new messages,
                  flare alerts, consent requests, likes received, and
                  marketing).
                </ListItem>
                <ListItem>
                  Control device location permissions through your operating
                  system settings.
                </ListItem>
                <ListItem>
                  Unmatch or block other users, and report content or accounts.
                </ListItem>
                <ListItem>
                  Delete your account, which initiates deletion of your
                  associated data as described in Section 5, subject to the
                  retention exceptions stated there.
                </ListItem>
              </List>

              <SubTitle>8.2 Legal Rights</SubTitle>
              <P>
                Depending on where you live, you may have rights to access,
                correct, delete, or receive a copy of your personal information,
                to object to or restrict certain processing, to withdraw
                consent, and to appeal a decision about your request. You will
                not be discriminated against for exercising these rights. To
                make a request, contact us using the details in Section 13; we
                will verify your identity before acting and respond within the
                timeframes required by applicable law.
              </P>
              <P>
                Depending on your state of residence, U.S. privacy laws may give
                you some or all of the following rights: to{" "}
                <strong>know</strong> what personal information we have
                collected and how we use and share it; to{" "}
                <strong>access</strong> or obtain a portable copy of it; to{" "}
                <strong>correct</strong> inaccurate information; to{" "}
                <strong>delete</strong> your information; and to{" "}
                <strong>limit the use of sensitive personal information</strong>
                . Because NudR does not sell your personal information and does
                not share it for cross-context behavioral advertising, there is
                nothing to opt out of in that respect; if that ever changes, we
                will provide a clear opt-out mechanism and update this Policy
                first.
              </P>
              <P>
                To exercise any of these rights, contact us using the details in
                Section 13. We will take reasonable steps to verify your
                identity before acting on your request, and we will respond
                within the timeframe required by applicable law. You may use an
                authorized agent to submit a request where the law permits. If
                we decline your request, you may appeal that decision by
                replying to our response, and we will inform you of the outcome.
                We will not discriminate against you for exercising any of these
                rights.
              </P>
            </Section>

            {/* 9. Comms */}
            <Section id="comms">
              <SectionTitle>
                <span>09</span>Communications and Notifications
              </SectionTitle>
              <P>
                We send service, security, and transactional messages (such as
                verification and password-reset emails, and
                match/message/consent notifications) that are necessary to
                operate the Service; these are not promotional and you cannot
                opt out of them while you maintain an account. You can control
                push notifications and marketing messages through your
                notification preferences and device settings, and you can
                unsubscribe from marketing emails using the link they contain.
              </P>
            </Section>

            {/* 10. Transfers */}
            <Section id="transfers">
              <SectionTitle>
                <span>10</span>International Data Transfers
              </SectionTitle>
              <P>
                NudR is based in the United States, and our cloud infrastructure
                and service providers store and process information in the
                United States and potentially in other countries where those
                providers operate. If you access the Service from outside the
                United States, you understand that your information will be
                transferred to, stored, and processed in the United States,
                where data-protection laws may differ from those in your
                location. Where required by applicable law, we use appropriate
                safeguards and recognized legal transfer mechanisms to protect
                information that moves across borders, and we take reasonable
                steps to ensure it remains protected consistent with this
                Policy.
              </P>
            </Section>

            {/* 11. Children */}
            <Section id="children">
              <SectionTitle>
                <span>11</span>Children's Privacy
              </SectionTitle>
              <P>
                The Service is strictly for adults 18 and older. We do not
                knowingly collect personal information from anyone under 18, and
                we verify date of birth at signup. If you believe a minor has
                provided us information, contact us at the address in Section 13
                and we will take appropriate steps to delete it.
              </P>
            </Section>

            {/* 12. Changes */}
            <Section id="changes">
              <SectionTitle>
                <span>12</span>Changes to This Policy
              </SectionTitle>
              <P>
                We may update this Policy from time to time. If we make a
                material change, we will provide notice through the Service, by
                email, or by another reasonable method before the change takes
                effect, as required by applicable law. The "Last Updated" date
                at the top reflects the most recent revision. Your continued use
                of the Service after a revised Policy takes effect constitutes
                acceptance of the updated Policy, except where additional
                consent is required by law.
              </P>
            </Section>

            {/* 13. Contact */}
            <Section id="contact">
              <SectionTitle>
                <span>13</span>How to Contact Us
              </SectionTitle>
              <P>
                If you have questions about this Policy or wish to exercise your
                rights, contact us at:
              </P>
              <ContactList>
                <ContactRow>
                  <b>Privacy / Legal:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Security and Trust & Safety:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> 1105 N Buena Vista Street, Burbank,
                  CA 91505
                </ContactRow>
                <ContactRow>
                  <b>Data Protection Officer / Privacy contact:</b>{" "}
                  <a href="mailto:privacy@nudr.app">privacy@nudr.app</a>
                </ContactRow>
              </ContactList>
              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
